// 에디터 지면 헤더 목차(어코디언) — 게시글 상세의 header_spy 와 동일한 로직을 클라이언트에서 재현
//
// 상세(서버)는 ERB 로 한 번 렌더되지만, 에디터는 블록이 실시간으로 추가/삭제/수정되므로
// 같은 트리 구성 규칙을 JS 로 포팅해 동적으로 다시 그린다.
//
// ⚠️ 트리 구성/접힘 규칙은 app/helpers/spaces/posts_helper.rb 의
//    build_header_toc_nodes / header_spy_default_collapsed? 와 1:1 로 대응한다.
//    한쪽을 고치면 다른 쪽도 함께 고쳐 동작이 갈라지지 않게 유지할 것.

// header_spy 전체 항목 수가 이 값을 넘으면 자식 섹션을 기본 접힘으로 노출한다.
// (Ruby: HEADER_SPY_COLLAPSE_THRESHOLD)
export const HEADER_SPY_COLLAPSE_THRESHOLD = 12;

// header 목록에서 "글에 실제로 쓰인 레벨" 기준으로 트리를 만든다.
// 사용자마다 h3만 쓰거나 h1+h2 를 쓰는 등 조합이 제각각이므로 고정 레벨이 아니라
// 등장한 레벨들을 정렬해 depth(0,1,2...)로 정규화한 뒤 상하 관계를 판정한다.
//
// headers: [{ level, id, text }, ...]  (문서 순서)
// 반환: [{ id, text, depth, children: [...] }, ...]  (상위 섹션이 root)
export function buildHeaderTocNodes(headers, { maxLevel = 3 } = {}) {
    if (!headers || headers.length === 0) return [];

    const items = headers
        .filter((h) => Number(h.level) <= maxLevel)
        .map((h) => ({ rawLevel: Number(h.level) || 1, id: h.id, text: h.text }));
    if (items.length === 0) return [];

    // 등장한 레벨만 정렬해 depth 로 정규화 (h1+h3 처럼 중간 레벨이 비어도 자연스럽게 인접 depth 로)
    const sortedLevels = [...new Set(items.map((it) => it.rawLevel))].sort((a, b) => a - b);
    const depthOf = new Map(sortedLevels.map((lv, idx) => [lv, idx]));

    const roots = [];
    const stack = [];
    for (const item of items) {
        const node = { id: item.id, text: item.text, depth: depthOf.get(item.rawLevel), children: [] };
        while (stack.length && stack[stack.length - 1].depth >= node.depth) stack.pop();
        (stack.length ? stack[stack.length - 1].children : roots).push(node);
        stack.push(node);
    }
    return roots;
}

// 트리에 포함된 전체 항목 수 (접기 기본값 판정에 사용)
export function headerTocCount(nodes) {
    return nodes.reduce((sum, node) => sum + 1 + headerTocCount(node.children), 0);
}

// 항목이 많을 때만 자식 섹션을 기본 접힘으로 — 항목이 적으면 접을 이유가 없으므로 펼쳐둔다.
export function headerSpyDefaultCollapsed(roots) {
    return headerTocCount(roots) > HEADER_SPY_COLLAPSE_THRESHOLD;
}

// 헤더 텍스트 → 목차 라벨 (태그 제거 + %name% 치환)
function tocLabel(text) {
    return String(text || "").replace(/%name%/g, "구독자").trim();
}

// 상세 header_spy 와 동일한 텍스트 스타일
const LABEL_CSS =
    "px-3 py-2 text-sm leading-5 font-medium text-slate-600 dark:text-slate-300 focus:outline-none transition ease-in-out duration-150 truncate";

// 에디터의 헤더 블록을 실시간으로 추적해 목차 어코디언을 그려주는 컨트롤러.
// MutationObserver 로 블록 추가/삭제/수정을 감지해 다시 그린다.
export class EditorHeaderToc {
    constructor({ editor, holderElement, container, maxLevel = 3 }) {
        this.editor = editor;
        this.holderElement = holderElement;
        this.container = container;
        this.maxLevel = maxLevel;

        // 사용자가 직접 접거나 펼친 노드의 상태(id -> boolean). 다시 그릴 때 유지한다.
        this.openState = new Map();
        // 직전 렌더의 "구조" 시그니처(id+depth+자식유무+순서). 구조가 같으면 통째로 다시 그리지 않고
        // 라벨 텍스트만 제자리 갱신한다 → 헤더 타이핑 중 nav 전체가 깜빡이는 현상 방지.
        this._lastStructSig = null;
        // 라벨 <p> 요소 캐시(id -> p). 제자리 텍스트 갱신에 사용.
        this.labelEls = new Map();
        // 입력 중 헤더가 일시적으로 0개로 보일 때 즉시 지우지 않기 위한 지연 타이머.
        this._emptyTimer = null;
        // 한글 등 IME 조합 중 여부 — 조합 중에는 라벨 갱신을 보류해 깜빡임을 막는다.
        this._composing = false;

        this._scheduled = false;
        this._onMutation = this._onMutation.bind(this);
        this._onCompositionStart = () => { this._composing = true; };
        this._onCompositionEnd = () => { this._composing = false; this._onMutation(); };
    }

    start() {
        if (!this.container) return;

        this.observer = new MutationObserver(this._onMutation);
        this.observer.observe(this.holderElement, {
            childList: true,
            subtree: true,
            characterData: true,
        });

        // IME 조합 시작/끝 추적 (조합 중 갱신 보류 → 조합 종료 시 한 번 갱신)
        this.holderElement.addEventListener("compositionstart", this._onCompositionStart);
        this.holderElement.addEventListener("compositionend", this._onCompositionEnd);

        this.render();
    }

    destroy() {
        if (this.observer) {
            this.observer.disconnect();
            this.observer = null;
        }
        this.holderElement.removeEventListener("compositionstart", this._onCompositionStart);
        this.holderElement.removeEventListener("compositionend", this._onCompositionEnd);
        if (this._emptyTimer) {
            clearTimeout(this._emptyTimer);
            this._emptyTimer = null;
        }
        if (this._rafId) cancelAnimationFrame(this._rafId);
        this.labelEls.clear();
        if (this.container) this.container.innerHTML = "";
    }

    _onMutation() {
        // 입력 중 과도한 재렌더를 막기 위해 다음 프레임으로 디바운스
        if (this._scheduled) return;
        this._scheduled = true;
        if (this._rafId) cancelAnimationFrame(this._rafId);
        this._rafId = requestAnimationFrame(() => {
            this._scheduled = false;
            this.render();
        });
    }

    // 에디터 최상위 블록 중 헤더만 추려 { level, id, text } 목록으로 — 상세가 editor_data["blocks"]
    // 최상위만 보는 것과 동일하게, 칼럼 내부 헤더는 제외한다(blocks API 가 최상위만 반환).
    _collectHeaders() {
        const headers = [];
        let count;
        try {
            count = this.editor.blocks.getBlocksCount();
        } catch (e) {
            return headers;
        }
        for (let i = 0; i < count; i++) {
            const block = this.editor.blocks.getBlockByIndex(i);
            if (!block || block.name !== "header") continue;
            // 이 포크의 헤더 툴은 실제 <h1>~<h6> 가 아니라 <div class="ce-header" data-level="N"> 로 렌더한다.
            // 레벨은 dataset.level 에 들어있다. (h 태그도 혹시 모를 경우를 대비해 함께 탐색)
            const el = block.holder?.querySelector(".ce-header, h1, h2, h3, h4, h5, h6");
            if (!el) continue;
            const level = el.dataset && el.dataset.level
                ? parseInt(el.dataset.level, 10)
                : parseInt(el.tagName.substring(1), 10);
            headers.push({
                level: level || 1,
                id: block.id,
                text: el.textContent || "",
            });
        }
        return headers;
    }

    render() {
        // IME 조합 중에는 갱신을 보류 — 한글을 조합하는 매 단계마다 라벨을 다시 그리면 깜빡인다.
        // 조합이 끝나면(compositionend) 한 번 갱신된다.
        if (this._composing) return;

        const headers = this._collectHeaders();
        const roots = buildHeaderTocNodes(headers, { maxLevel: this.maxLevel });

        if (roots.length === 0) {
            // 진짜 헤더가 없는 건지, 입력 중 일시적으로 비어 보이는 건지 구분.
            // 직전에 헤더가 있었다면 바로 지우지 말고 잠깐 뒤 재확인 → 전체 깜빡임 방지.
            if (this._lastStructSig) {
                if (!this._emptyTimer) {
                    this._emptyTimer = setTimeout(() => {
                        this._emptyTimer = null;
                        if (this._collectHeaders().length === 0) this._clear();
                    }, 250);
                }
                return;
            }
            this._clear();
            return;
        }

        // 헤더가 다시 잡혔으니 대기 중이던 비움 예약 취소
        if (this._emptyTimer) {
            clearTimeout(this._emptyTimer);
            this._emptyTimer = null;
        }
        this.container.classList.remove("hidden");

        const structSig = this._structureSignature(roots);

        // 구조(헤더 개수/레벨/순서/계층)가 그대로면 라벨 텍스트만 제자리 갱신 → DOM 재생성 없음 = 깜빡임 없음.
        if (structSig === this._lastStructSig && this.container.querySelector("nav")) {
            this._updateLabels(roots);
            return;
        }

        // 구조가 바뀐 경우(헤더 추가/삭제/레벨 변경 등)에만 전체 재구성.
        this._lastStructSig = structSig;
        this.defaultCollapsed = headerSpyDefaultCollapsed(roots);
        this.labelEls = new Map();

        // sticky 는 컨테이너(grid 셀 직속 자식)에 있으므로 nav 는 일반 흐름으로 둔다.
        const nav = document.createElement("nav");
        nav.className = "bg-white dark:bg-stone-900 flex flex-col truncate";
        // 한글 입력(IME) 중 라벨이 갱신될 때 변수 웹폰트 리렌더로 깜빡이는 것을 막기 위해
        // nav 라벨은 즉시 사용 가능한 시스템 폰트로 렌더한다.
        nav.style.fontFamily =
            '-apple-system, BlinkMacSystemFont, "Apple SD Gothic Neo", "Noto Sans KR", "Malgun Gothic", sans-serif';
        roots.forEach((node) => nav.appendChild(this._renderNode(node)));

        this.container.replaceChildren(nav);
    }

    _clear() {
        this.container.innerHTML = "";
        this.container.classList.add("hidden");
        this._lastStructSig = null;
        this.labelEls.clear();
    }

    // 트리 구조 시그니처 — 텍스트는 제외하고 id/depth/자식유무/순서만 직렬화.
    // 텍스트만 바뀌면 시그니처가 동일 → 제자리 갱신 경로를 타게 된다.
    _structureSignature(roots) {
        const parts = [];
        const walk = (nodes) => {
            for (const n of nodes) {
                parts.push(`${n.id}:${n.depth}:${n.children.length ? 1 : 0}`);
                walk(n.children);
            }
        };
        walk(roots);
        return parts.join("|");
    }

    // 기존 라벨 <p> 의 텍스트만 갱신 (변경된 것만).
    _updateLabels(roots) {
        const walk = (nodes) => {
            for (const n of nodes) {
                const p = this.labelEls.get(n.id);
                const label = tocLabel(n.text);
                if (p && p.textContent !== label) p.textContent = label;
                walk(n.children);
            }
        };
        walk(roots);
    }

    // 노드 하나를 렌더 — 상세 _header_spy_node.html.erb 와 동일한 구조/동작
    _renderNode(node) {
        const hasChildren = node.children.length > 0;
        const indentPx = node.depth * 14;

        if (!hasChildren) {
            const row = document.createElement("div");
            row.className =
                "flex items-center hover:bg-stone-200 dark:hover:bg-stone-700 cursor-pointer";
            row.style.paddingLeft = `${indentPx}px`;
            row.addEventListener("click", () => this._scrollToBlock(node.id));

            const p = document.createElement("p");
            p.className = `pointer-events-none ${LABEL_CSS} flex-1 min-w-0`;
            p.textContent = tocLabel(node.text);
            this.labelEls.set(node.id, p);
            row.appendChild(p);
            return row;
        }

        const wrap = document.createElement("div");

        const row = document.createElement("div");
        row.className = "flex items-center hover:bg-stone-200 dark:hover:bg-stone-700";
        row.style.paddingLeft = `${indentPx}px`;

        // 라벨 영역: 클릭 시 이동 + 펼침
        const labelBox = document.createElement("div");
        labelBox.className = "flex-1 min-w-0 cursor-pointer";
        const p = document.createElement("p");
        p.className = `pointer-events-none ${LABEL_CSS}`;
        p.textContent = tocLabel(node.text);
        this.labelEls.set(node.id, p);
        labelBox.appendChild(p);

        // chevron 토글
        const toggle = document.createElement("button");
        toggle.type = "button";
        toggle.setAttribute("aria-label", "섹션 접기/펼치기");
        toggle.className =
            "shrink-0 w-8 self-stretch flex items-center justify-center text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 focus:outline-none";
        toggle.innerHTML =
            '<svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5 stroke-current transition-transform duration-150" width="24" height="24" viewBox="0 0 24 24" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><polyline points="9 6 15 12 9 18"/></svg>';

        row.appendChild(labelBox);
        row.appendChild(toggle);

        // 자식 컨테이너
        const childrenBox = document.createElement("div");
        node.children.forEach((child) => childrenBox.appendChild(this._renderNode(child)));

        // 초기 열림 상태: 사용자가 직접 바꿨으면 그 값을, 아니면 defaultCollapsed 기준
        const initialOpen = this.openState.has(node.id)
            ? this.openState.get(node.id)
            : !this.defaultCollapsed;

        const applyOpen = (open) => {
            this.openState.set(node.id, open);
            childrenBox.style.display = open ? "" : "none";
            toggle.querySelector("svg").classList.toggle("rotate-90", open);
            toggle.setAttribute("aria-expanded", String(open));
        };
        applyOpen(initialOpen);

        // 같은 레벨 형제는 자동 접힘 — 한 노드를 펼치면 형제(wrap)들의 자식을 닫는다.
        const openExclusively = () => {
            wrap.parentElement?.querySelectorAll(":scope > [data-toc-group]").forEach((sib) => {
                if (sib !== wrap && sib._tocApplyOpen) sib._tocApplyOpen(false);
            });
            applyOpen(true);
        };

        labelBox.addEventListener("click", () => {
            this._scrollToBlock(node.id);
            openExclusively();
        });
        toggle.addEventListener("click", () => {
            const next = !this.openState.get(node.id);
            if (next) openExclusively();
            else applyOpen(false);
        });

        wrap.setAttribute("data-toc-group", "");
        wrap._tocApplyOpen = applyOpen;
        wrap.appendChild(row);
        wrap.appendChild(childrenBox);
        return wrap;
    }

    _scrollToBlock(id) {
        if (!id) return;
        const safeId = window.CSS && CSS.escape ? CSS.escape(id) : id;
        const el = this.holderElement.querySelector(`.ce-block[data-id="${safeId}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
}
